import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import type {
	StudioSection,
	TimeChip,
	WeekView,
} from '@/features/scheduling/availability'
import { countOnDay, weekDays } from '@/features/scheduling/availability'
import { Header } from '@/ui/core/header'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { format, parseISO } from 'date-fns'
import { ScrollView, View } from 'react-native'
import { TimeChipButton } from './TimeChipButton'
import { PAD, PILL, SLOP, TYPE_INSET } from './layout'

/**
 * One studio's times on the chosen day, in a lane that swipes.
 *
 * On blush, which is what this app puts a card on when it is not the thing being led with. The
 * studios are the frame; the white pills inside them are the answer, and white on white gave the
 * two the same weight. It is also the surface the times were drawn for.
 *
 * The times run off the card's right edge rather than wrapping into rows, so a studio costs the
 * same height whether it holds two openings or twenty and the studio under it is always in the
 * same place. Wrapped, a busy morning pushed every other studio off the phone — and the studios
 * are the thing being compared, so they are what has to stay on screen.
 *
 * The lane's padding is the card's, which is what makes the swipe legible: a chip sits under the
 * rounded edge rather than stopping short of it, so there is visibly more to the right.
 *
 * The address is not on the line. It was two lines of small grey type per studio, repeated down
 * the screen, for a fact a member needs once — so it is behind the name, which opens the studio
 * with its address and a map.
 *
 * The name is the control and the ring sits at the card's far edge, where this app has ended a
 * row since its first chevron. Beside the name the mark travelled with it, so nothing lined up
 * down the right-hand side and a long name pushed its own mark about; in the corner they stack
 * whatever the names do. The whole line is the target, so the distance costs nothing to hit.
 *
 * A ring rather than a caret: the city line above uses a caret, and the two open different kinds
 * of thing — the city changes what the screen is showing, where this opens a sheet about one
 * studio. Lighter than the name, because it is the second thing on the line and not the first.
 *
 * No underline either: the city line is the one underlined thing here, and a card whose title is
 * also underlined puts two of them in a column and makes neither special.
 */
const Studio = ({
	studio,
	chips,
	selectedKey,
	onSelect,
	onOpenStudio,
}: {
	studio: StudioSection
	chips: TimeChip[]
	selectedKey?: string
	onSelect: (chip: TimeChip) => void
	onOpenStudio: () => void
}) => (
	<VStack gap={12} className="rounded-note bg-blush" style={{ paddingVertical: PAD }}>
		<Pressable
			onPress={onOpenStudio}
			accessibilityRole="button"
			accessibilityLabel={`${studio.name}. Address and map`}
			hitSlop={10}
			style={{ paddingHorizontal: PAD }}
		>
			<HStack className="items-center justify-between" gap={10}>
				<HStack className="shrink items-center" gap={7}>
					<Text size="body0" weight="bold" numberOfLines={1} className="shrink">
						{studio.name}
					</Text>
					{studio.isHomeStudio && (
						<View
							className="h-1.5 w-1.5 rounded-full"
							style={{ backgroundColor: colors.leaf }}
							accessibilityLabel="Your studio"
						/>
					)}
				</HStack>
				<Glyph name="info" size={18} color={colors.edge} />
			</HStack>
		</Pressable>
		{/* Centred, not stretched. A horizontal ScrollView's content container aligns its
		    children on the cross axis, and the default stretch was pulling the pills to the
		    lane's own height — they were drawing at thirty-nine points rather than the
		    forty-four they are set to, which is the whole reason they were grown. It also cost
		    the card two points of its bottom inset, so the pills sat closer to the bottom edge
		    than to the left one. */}
		<ScrollView
			horizontal
			showsHorizontalScrollIndicator={false}
			contentContainerStyle={{ gap: 8, paddingHorizontal: PAD, alignItems: 'center' }}
		>
			{chips.map((chip) => (
				<TimeChipButton
					key={chip.key}
					chip={chip}
					isSelected={chip.key === selectedKey}
					onPress={() => onSelect(chip)}
				/>
			))}
		</ScrollView>
	</VStack>
)

/**
 * What is open on one day: the studios that can see you, and one line for the ones that cannot.
 *
 * The quiet studios were a dimmed row each, which on a day when three studios in five are shut
 * spent a third of the screen printing the same three words. Collapsed into a sentence they cost
 * one line and still answer the only question a shut studio raises — whether it is the one a
 * member was looking for — because the line names them.
 *
 * It names them rather than counting them. "Nothing at 3 other studios" tells a member three
 * exist without saying which, so the studio they came for is unaccounted for either way, which
 * is the same failure as the row that said nothing.
 *
 * Nothing here looks past the week. Each studio carries the seven days of the week that was
 * fetched and no more, so when a shut studio is next open is knowable only inside that week —
 * and a line promising "next Thursday" from a screen that cannot see next Monday would be a
 * guess dressed as a fact. The green dots on the strip say where this week has something;
 * beyond it, the arrows.
 */

/**
 * One way out of an empty day: another day this week, or the week after it.
 *
 * The same object for both, because they are the same offer — somewhere else to look — and only
 * the distance differs.
 *
 * Ink, not white. White is what this screen gives a time: one of several things on offer, to be
 * weighed against its neighbours. These appear where there is nothing to weigh — the only thing
 * to do on a day or a week that holds nothing — and the app draws the one action a screen leads
 * with in ink.
 *
 * A day here is only ever inside the week already fetched, and never one that has passed. Which
 * days those are is exactly what the strip's green dots say, so this is the same fact offered as
 * a target rather than a mark. Past Sunday is what the week button is for.
 *
 * No arrow on either. An arrow says "onward", and half of these days are earlier in the week
 * than the one being looked at — a date is not a direction.
 */
const Try = ({ label, onPress }: { label: string; onPress: () => void }) => (
	<Pressable
		onPress={onPress}
		accessibilityRole="button"
		accessibilityLabel={label}
		hitSlop={SLOP}
		className="flex-row items-center rounded-full bg-ink px-5"
		style={{ height: PILL }}
	>
		<Text size="body1" weight="bold" tone="white">
			{label}
		</Text>
	</Pressable>
)

/**
 * What is not on offer, as one object at whatever size the fact is.
 *
 * A studio shut on a Tuesday, a Tuesday with nothing anywhere, and a week with nothing at all
 * are the same fact at three sizes, so they are the same object and the screen never swaps one
 * kind of thing for another as a week empties out.
 *
 * No card. A panel is what this screen puts an offer in — a studio's times, a day's seven — and
 * a panel holding the news that there is no offer promises with its shape what its words then
 * take back. On the ground it is the page saying something rather than a thing on the page, and
 * the heading is set at the size the week above it is, because at that moment it is what the
 * screen is about.
 *
 * It never dead-ends. Every way out sits in one row of identical buttons — the days left in this
 * week, then the week after it — so a member who lands on an empty day always has somewhere to
 * go without having to work out that the arrows at the top would do it. No label over them: they
 * say "Try Tue 6" and "Try Next Week", which is the label.
 *
 * Exported, because a week with nothing in it is this same object one size up. It was a photo
 * with the brand at full voice, which made an occasion of a quiet week and then offered one way
 * out; this says the same thing in the same shape as the day below it, and a member reads one
 * kind of answer whether the gap is a day or a week.
 */
export const Quiet = ({
	title,
	body,
	ways,
	onChooseDay,
	onNextWeek,
}: {
	title?: string
	body: string
	ways: string[]
	onChooseDay: (date: string) => void
	onNextWeek?: () => void
}) => (
	// A heading wants room above it and little below: the line under it is the same sentence
	// continued, where what is above is a different thing entirely.
	<VStack
		style={{ paddingHorizontal: TYPE_INSET, marginTop: title ? 14 : 0 }}
		gap={title ? 6 : 0}
	>
		{/* h3, in title case. At h2 it was set in caps at the size of the week above it, which
		    made the absence the loudest thing on the screen — a shout where the news is only that
		    a week is quiet. Title case is how this app names a thing rather than announces it:
		    "Choose a City", "Pick a Week", and now this. */}
		{!!title && <Header variant="h3">{title}</Header>}
		<Text size="body1" tone="soft">
			{body}
		</Text>
		{(ways.length > 0 || !!onNextWeek) && (
			<HStack className="mt-4 flex-wrap" gap={8}>
				{ways.map((date) => (
					<Try
						key={date}
						label={`Try ${format(parseISO(`${date}T00:00:00`), 'EEE d')}`}
						onPress={() => onChooseDay(date)}
					/>
				))}
				{!!onNextWeek && <Try label="Try Next Week" onPress={onNextWeek} />}
			</HStack>
		)}
	</VStack>
)

/** "A", "A or B", "A, B or C" — the app's own list, without the serial comma. */
const listNames = (names: string[]) =>
	names.length < 2
		? names[0] ?? ''
		: `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`

/** How many studios are worth naming before the rest become a number. */
const NAMED = 3

/**
 * The shut studios, named while naming them is still reading and counted once it is not.
 *
 * Three names is a line. A fourth and a fifth turn the sentence into an inventory, and the
 * member looking for their own studio has to read all of it to find out theirs is not there —
 * which the count answers just as well and in four words.
 */
const shutList = (names: string[]) => {
	if (names.length <= NAMED) {
		return listNames(names)
	}
	const rest = names.length - NAMED
	return `${names.slice(0, NAMED).join(', ')} and ${rest} other ${
		rest === 1 ? 'studio' : 'studios'
	}`
}

/**
 * What is open on one day.
 */
export const DayTimes = ({
	view,
	date,
	selectedKey,
	onSelect,
	onOpenStudio,
	onChooseDay,
	onNextWeek,
}: {
	view: WeekView
	date?: string
	selectedKey?: string
	onSelect: (chip: TimeChip) => void
	onOpenStudio: (studio: StudioSection) => void
	onChooseDay: (date: string) => void
	onNextWeek: () => void
}) => {
	if (!date) {
		return null
	}

	const day = parseISO(`${date}T00:00:00`)
	const count = `${view.studios.length} ${
		view.studios.length === 1 ? 'studio' : 'studios'
	}`
	const entries = view.studios.map((studio) => ({
		studio,
		chips: studio.days.find((entry) => entry.date === date)?.chips ?? [],
	}))
	const open = entries.filter((entry) => entry.chips.length > 0)
	const shut = entries.filter((entry) => entry.chips.length === 0)
	// The other days of this week that hold something — never a date past it.
	const elsewhere = weekDays(view.weekStart).filter(
		(other) => other !== date && countOnDay(view, other) > 0
	)

	// No date heading. The strip above is the date, filled and in ink, so naming it again
	// underneath was the same answer twice.
	return (
		<VStack gap={12}>
			{open.map(({ studio, chips }) => (
				<Studio
					key={studio.locationId}
					studio={studio}
					chips={chips}
					selectedKey={selectedKey}
					onSelect={onSelect}
					onOpenStudio={() => onOpenStudio(studio)}
				/>
			))}
			{shut.length > 0 &&
				(open.length === 0 ? (
					/* Nothing anywhere: the block is the screen, and it carries the way out.
					   Naming all five studios here would be the same statement as saying the day
					   is closed, and the longer way to say it. */
					<Quiet
						title={`No Openings on ${format(day, 'EEEE')}`}
						body={
							elsewhere.length > 0
								? `No available times at any of the ${count} in the city. Try another day, or a different week.`
								: `No available times at any of the ${count} in the city for the rest of the week.`
						}
						ways={elsewhere}
						onChooseDay={onChooseDay}
						onNextWeek={onNextWeek}
					/>
				) : (
					/* Some of them: a line on the ground, not a card.
					
					   A blush block here sat directly under two blush studio cards and read as a
					   third one that happened to hold no times — the shape promised an offer the
					   words then took back. On the ground it is plainly a footnote to the cards
					   rather than one of them, and it keeps their inset so the column of type
					   still lines up. */
					<Text
						size="body1"
						tone="soft"
						style={{ paddingHorizontal: PAD, paddingTop: 2 }}
					>
						{`No available times on ${format(day, 'EEEE')} at ${shutList(
							shut.map(({ studio }) => studio.name)
						)}.`}
					</Text>
				))}
		</VStack>
	)
}
